// File: typescript/apps/front-end/src/main/react/thoughts/ThoughtInDiscussionCardDisplayer.tsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Check, LayoutList, Minus, Pin, Sparkles, TextQuote } from "lucide-react";
import ThoughtDisplayerInDiscussion from "@/main/react/thoughts/ThoughtDisplayerInDiscussion";
import { Localizer } from "@/main/react/Types";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { GroupDTO } from "@/generated-client";
import ThoughtTextEditor from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import type { EditorHandle as ThoughtTextEditorHandle } from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import { applyQuoteWhenReady, showThoughtTextEditorDialog } from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type { DialogController } from "@/main/react/Dialog/Dialog";
import ThoughtReplyTree, { ReplyTreeCommonProps } from "@/main/react/thoughts/ThoughtReplyTree";
import { threadConnectorLineClass } from "@/main/react/thoughts/replyThreadConnectorStyles";
import { thoughtChainHref } from "@/main/react/thoughts/thoughtChainRoute";
import { MessagesSquare } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { MetaHandler } from "@/main/common/MetaHandler";
import { scrollElementIntoViewWithOffset } from "@/main/common/ScrollUtils";
import { Badge } from "@/components/ui/badge";
import { OpeningThoughtWithGroupMatchesDTO } from "@/main/react/discussion/OpeningThoughtGroupTypes";
import { parsePassageAnchorState } from "@/main/react/discussion/passage/mapBlocksToDom";
import { scrollToPassageBlock } from "@/main/react/discussion/passage/passageBlockRegistry";
import { FirstReadAuthorReport } from "@/main/react/thoughts/FirstReadAuthorReport";
import type { QuestionStateView } from "@/main/common/QuestionFacetTypes";
import type { ThoughtBoardPlacementFields } from "@/main/common/BoardFacetThoughtTypes";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import { INSERTED_ITEM_MOTION_ENTER_DURATION_MS } from "@/main/react/component/InsertedItemMotion";
import { MindChangedComposer } from "@/main/react/thoughts/MindChangedComposer";
import { ClientFactory } from "@/main/common/ClientFactory";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { createClientUuid } from "@/main/util/createClientUuid";
import { getSafeToastMessageFromError } from "@/main/common/ApiUtils";
import { toast } from "sonner";
import logger from "@/lib/logger";
import { THREAD_SHORTCUT_REPLY_EVENT } from "@/main/react/thoughts/ThreadKeyboardShortcuts";

function localize(localizer: Localizer, key: LocalizationKey, ...args: (string | number)[]): string {
    return localizer(key, ...args);
}

// Renders one top-level ("opening") argument plus its recursive reply tree. Replies are an open,
// arbitrary-depth comment tree (threaded-comments migration): the opening argument's "Discuss"
// trigger opens an inline editor that publishes a reply directly under it, and ThoughtReplyTree
// lazily loads/nests the rest. The legacy 1:1 conversation rendering has been retired here.
export default function ThoughtInDiscussionCardDisplayer(props: {
    openingArgument: OpeningThoughtWithGroupMatchesDTO;
    loggedInUsername?: string;
    discussionSlug: string;
    // Host post title, for the share payload only (BL-083). Optional: an un-threaded mount keeps the
    // pre-2026-08-07 payload shape rather than sending an empty title.
    discussionTitle?: string;
    agreementAgreementTitle: string;
    disagreementAgreementTitle: string;
    localizer: Localizer;
    discussionPublicId: string;
    discussionGroupsByPublicId?: Record<string, GroupDTO>;
    selectedGroupPublicIds?: string[];
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
    displayMode?: "NORMAL" | "OPENING_HIGHLIGHTED";
    // How many reply layers nest inline before "Continue this thread". The main list caps at 2 so a
    // deep back-and-forth doesn't sprawl amongst sibling top-level arguments; the focused-comment
    // view passes 3 (a fresh budget for the reply you navigated into). Purely a rendering budget —
    // reply nesting itself is uncapped (2026-07-25), so this is what keeps a deep thread readable.
    replyTreeMaxDepth?: number;
    activeConversationPublicId?: string | null;
    onViewConversationClicked?: (conversationPublicId: string, argumentPublicId?: string) => void;
    // When a "reply to your argument" notification is opened (URL carries ?reply=1), the discussion
    // page threads this to the focused/highlighted card so its reply composer auto-opens once —
    // turning the notification into a one-tap counter-reply. Only the highlighted card receives it.
    autoOpenReply?: boolean;
    // Editorial pin: the id of the thought currently holding the discussion's single pin slot, and the
    // callback that re-fetches once it moves.
    pinnedThoughtPublicId?: string | null;
    onPinChanged?: () => void;
    // QUESTION facet: this card is an ANSWER. It owns the accepted flag for both the badge here and the
    // Accept/Unaccept controls in the action row, so the two can never disagree; `onAcceptChanged` tells
    // the owning list to re-fetch page 1 and re-sort.
    isQuestion?: boolean;
    onAcceptChanged?: (view: QuestionStateView) => void;
    /**
     * CHANGED MY MIND: the POST author's username, which is who the badge names.
     *
     * Only the post author can mark, so the badge always names them. Deriving it from this thought's
     * own author would name the recipient of the credit rather than the person whose mind moved,
     * which is the opposite sentence.
     */
    discussionAuthorUsername?: string;
}) {
    const {
        openingArgument,
        loggedInUsername,
        discussionSlug,
        discussionTitle,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        localizer,
        discussionPublicId,
        discussionGroupsByPublicId = {},
        selectedGroupPublicIds = [],
        isSensitive = false,
        suppressSpoilerGuards = false,
        displayMode = "NORMAL",
        replyTreeMaxDepth = 2,
        autoOpenReply = false,
        pinnedThoughtPublicId,
        onPinChanged,
        isQuestion = false,
        onAcceptChanged,
        discussionAuthorUsername,
    } = props;

    const isMobile = useIsMobile();
    const [displayEditor, setDisplayEditor] = useState(false);
    const [replyTreeReloadToken, setReplyTreeReloadToken] = useState(0);
    const [replyTreeCount, setReplyTreeCount] = useState(0);
    // Clicking the opening argument's thread rail collapses its reply thread; ThoughtReplyTree then
    // shows a "Show N replies" affordance to re-expand. Keeps the opening comment itself in view,
    // since it is the discussion's primary content.
    const [repliesCollapsed, setRepliesCollapsed] = useState(false);
    // Hover state of the opening argument's rail (its trunk line). Set by the opening collapse-rail
    // button; lights the trunk line + the first-level reply elbows, same per-rail model as nested nodes.
    const [openingRailHovered, setOpeningRailHovered] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const inlineEditorCloseTimeoutRef = useRef<number | null>(null);
    // On mobile the reply editor opens as a bottom-sheet dialog instead of expanding inline; keep its
    // controller so re-tapping "Discuss" reuses the same mounted dialog and we can clean it up on unmount.
    const replyDialogRef = useRef<DialogController | null>(null);

    const openingArgumentPublicId = openingArgument.openingArgument?.publicId;
    // Null when either segment is missing — the render gates on this rather than on the id's
    // truthiness, because the string "null" is truthy and was exactly what reached prod.
    const chainHref = thoughtChainHref(discussionSlug, openingArgumentPublicId);

    useEffect(() => {
        return () => {
            if (inlineEditorCloseTimeoutRef.current != null) {
                window.clearTimeout(inlineEditorCloseTimeoutRef.current);
                inlineEditorCloseTimeoutRef.current = null;
            }
            try {
                replyDialogRef.current?.cleanup();
            } catch {
                // ignore teardown errors
            }
            replyDialogRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (displayMode === "OPENING_HIGHLIGHTED" && rootRef.current) {
            scrollElementIntoViewWithOffset(rootRef.current, { attempts: 1 }).catch(() => {
                /* ignore */
            });
        }
    }, [displayMode]);

    const matchedDiscussionGroups = useMemo(() => {
        if (selectedGroupPublicIds.length === 0) {
            return [];
        }

        return (openingArgument.matchedDiscussionGroupPublicIds ?? [])
            .filter((groupPublicId) => selectedGroupPublicIds.includes(groupPublicId))
            .map((groupPublicId) => discussionGroupsByPublicId[groupPublicId])
            .filter(Boolean);
    }, [discussionGroupsByPublicId, openingArgument.matchedDiscussionGroupPublicIds, selectedGroupPublicIds]);

    const handleInlineReplyPublished = React.useCallback(() => {
        if (inlineEditorCloseTimeoutRef.current != null) {
            window.clearTimeout(inlineEditorCloseTimeoutRef.current);
        }

        // Refetch the reply tree so the new top-level reply appears, then close the editor.
        setReplyTreeReloadToken((token) => token + 1);
        inlineEditorCloseTimeoutRef.current = window.setTimeout(() => {
            setDisplayEditor(false);
            inlineEditorCloseTimeoutRef.current = null;
        }, INSERTED_ITEM_MOTION_ENTER_DURATION_MS);
    }, []);

    const openMobileReplyDialog = React.useCallback(() => {
        if (!openingArgumentPublicId) {
            return;
        }
        // Reuse the existing (kept-mounted) dialog if it's still mounted; otherwise open a fresh one.
        if (replyDialogRef.current) {
            replyDialogRef.current.open();
            return;
        }
        replyDialogRef.current = showThoughtTextEditorDialog({
            localizer,
            ariaLabelledBy: "discussion-inline-reply-editor-replying-to",
            isUserLoggedIn: MetaHandler.isUserLoggedIn(),
            loggedInUsername,
            discussionPublicId,
            parentArgumentPublicId: openingArgumentPublicId,
            agreementAgreementTitle,
            disagreementAgreementTitle,
            replyingToUsername: openingArgument.openingArgument?.username,
            playwrightIdPrefix: "discussion-inline-reply-editor",
            onThoughtPublished: () => handleInlineReplyPublished(),
        });
    }, [
        openingArgumentPublicId,
        localizer,
        loggedInUsername,
        discussionPublicId,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        openingArgument.openingArgument?.username,
        handleInlineReplyPublished,
    ]);

    const handleDiscussClicked = (_argumentPublicId?: string) => {
        if (isMobile) {
            openMobileReplyDialog();
            return;
        }
        // A publish schedules a close 260ms out (INSERTED_ITEM_MOTION_ENTER_DURATION_MS) so the composer
        // does not vanish under the new reply's insert animation. A click landing inside that window can
        // only mean "write another one" — so cancel the pending close and OPEN, rather than toggling a
        // state that is still `true` and thereby closing the composer the user just asked for. Mobile
        // never had this because openMobileReplyDialog() is idempotent-open with no false branch.
        // Design §10.8; pinned by thought-publish-directed-abuse-tier.spec.mjs, which replies twice.
        if (inlineEditorCloseTimeoutRef.current != null) {
            window.clearTimeout(inlineEditorCloseTimeoutRef.current);
            inlineEditorCloseTimeoutRef.current = null;
            setDisplayEditor(true);
            return;
        }
        setDisplayEditor((v) => !v);
    };

    // One-tap counter-reply: when opened from a "reply to your argument" notification (?reply=1),
    // auto-open this card's reply composer exactly once, after its argument has resolved. The
    // OPENING_HIGHLIGHTED display mode already scrolls it into view. Mobile opens the bottom-sheet
    // dialog; desktop opens the inline editor.
    const autoOpenReplyFiredRef = useRef(false);
    useEffect(() => {
        if (!autoOpenReply || autoOpenReplyFiredRef.current || !openingArgumentPublicId) {
            return;
        }
        autoOpenReplyFiredRef.current = true;
        if (isMobile) {
            openMobileReplyDialog();
        } else {
            setDisplayEditor(true);
        }
    }, [autoOpenReply, openingArgumentPublicId, isMobile, openMobileReplyDialog]);

    // FI-584: "Quote" on a selection inside this thought opens THIS thought's composer with the text quoted,
    // through the same open path as the reply button. The editor's handle may not exist yet (the inline editor
    // mounts on this state change; the sheet opens a tick later), so the quote waits for it.
    const inlineEditorRef = useRef<ThoughtTextEditorHandle | null>(null);
    const openReplyWithQuote = React.useCallback((selectionText: string) => {
        if (!openingArgumentPublicId || !selectionText) {
            return;
        }
        if (isMobile) {
            openMobileReplyDialog();
            (replyDialogRef.current as (DialogController & { addQuote?: (text?: string) => void }) | null)
                ?.addQuote?.(selectionText);
            return;
        }
        if (inlineEditorCloseTimeoutRef.current != null) {
            window.clearTimeout(inlineEditorCloseTimeoutRef.current);
            inlineEditorCloseTimeoutRef.current = null;
        }
        setDisplayEditor(true);
        applyQuoteWhenReady(() => inlineEditorRef.current, selectionText);
    }, [isMobile, openMobileReplyDialog, openingArgumentPublicId]);

    // FI-586: the thread list's `r` shortcut asks the focused card to open its reply composer. Same open path
    // as the reply button, but always OPEN (never a toggle that could close a composer already showing).
    useEffect(() => {
        const node = rootRef.current;
        if (!node || !openingArgumentPublicId) {
            return;
        }
        const onShortcutReply = () => {
            if (isMobile) {
                openMobileReplyDialog();
                return;
            }
            if (inlineEditorCloseTimeoutRef.current != null) {
                window.clearTimeout(inlineEditorCloseTimeoutRef.current);
                inlineEditorCloseTimeoutRef.current = null;
            }
            setDisplayEditor(true);
        };
        node.addEventListener(THREAD_SHORTCUT_REPLY_EVENT, onShortcutReply);
        return () => node.removeEventListener(THREAD_SHORTCUT_REPLY_EVENT, onShortcutReply);
    }, [isMobile, openMobileReplyDialog, openingArgumentPublicId]);

    const replyTreeCommon: ReplyTreeCommonProps = {
        discussionPublicId,
        discussionSlug,
        discussionTitle,
        discussionAuthorUsername,
        localizer,
        loggedInUsername,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        isSensitive,
        suppressSpoilerGuards,
    };

    // Flat feed surface (X/Reddit style): no card chrome. Adjacent opening arguments are separated
    // by a hairline divider only; the transient OPENING_HIGHLIGHTED state keeps a soft rounded
    // highlight instead. Do NOT touch the inner structure/avatar/connector.
    //
    // DISCUSSION-52 — the ring is raised from `/35` to `/80`. This ring is the ONLY thing on a thought
    // permalink saying which of ~110 thoughts the link pointed at: no label, no badge, no heading. At
    // `/35` it measured 1.77:1 light and 1.80:1 dark against the page, roughly HALF an ordinary card
    // hairline (3.91:1) and well under WCAG 1.4.11's 3:1 for meaningful non-text. Composited against
    // the page background (#f9fafb light, #0d0e11 dark) `/80` computes to 4.39:1 and 4.63:1 — the same
    // order as the card hairline, so the marker reads as a deliberate frame rather than a tint.
    // ⚠️ Those two numbers are COMPUTED from --primary-action (hsl(214,90%,38%) light,
    // hsl(214,72%,64%) dark) over the sampled page colours; they are not sampled from a render, which
    // is exactly what this row's `Pending visual` owes. The fill stays at `/5`: raising the fill would
    // dim the thought's own text, and the row asks for a perceivable EDGE, not a louder surface.
    // Flat-X keeps shadows for floating layers, so the edge is a ring and not elevation.
    const rootClassName = `min-w-0 max-w-full px-3 py-3 ${displayMode === "OPENING_HIGHLIGHTED" ? "overflow-hidden rounded-2xl bg-primary/5 ring-1 ring-primary/80" : "border-b border-[color-mix(in_srgb,var(--line-color)_55%,transparent)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"}`;

    // The pinned badge. Two keys (design D4): the self-pin — an author pinning their own clarification
    // — is the primary use case, and "Pinned by <name>" reads badly on a card that already names that
    // person as the author. `pinnedByUsername` is null when the pinner's account has been erased, which
    // falls back to [Deleted User] rather than dropping the badge; this repo has already taken a
    // production hit from the erased-author case.
    const pinnedByUsername = openingArgument.openingArgument?.pinnedByUsername?.trim() || null;
    const isPinnedByItsOwnAuthor = pinnedByUsername != null
        && pinnedByUsername === openingArgument.openingArgument?.username;
    const pinnedBadgeLabel = !openingArgument.openingArgument?.pinned
        ? null
        : isPinnedByItsOwnAuthor
            ? localize(localizer, "discussion.thoughts.pinned.badge.own")
            : localize(localizer, "discussion.thoughts.pinned.badge", pinnedByUsername ?? localizer("literal.deleted_user"));

    // The accepted-answer flag. It comes off THIS answer's own `accepted` field, which the server has
    // already resolved through the answer's visibility — never from comparing this id against the
    // discussion's `acceptedThoughtPublicId`, a pointer that outlives a soft delete (Q-D10).
    //
    // The override exists only between a successful accept/unaccept and the list re-fetch that follows it,
    // so the badge lands on the card the reader actually pressed instead of appearing a round trip later.
    // It is written from the server's response, and the effect below clears it in ONE direction when a
    // fresh DTO arrives — never a two-way sync between the two (rule 31).

    const dtoAccepted = Boolean(openingArgument.openingArgument?.accepted);
    const [acceptedOverride, setAcceptedOverride] = useState<boolean | null>(null);
    useEffect(() => {
        setAcceptedOverride(null);
    }, [dtoAccepted, openingArgumentPublicId]);
    const isAccepted = acceptedOverride ?? dtoAccepted;
    const handleAcceptedChanged = (nextAccepted: boolean, view: QuestionStateView) => {
        setAcceptedOverride(nextAccepted);
        onAcceptChanged?.(view);
    };

    // CHANGED MY MIND. The card owns the flag, the composer and the write, for the same reason it owns
    // `accepted`: it also draws the badge, and two copies of one boolean in a parent/child pair is how
    // they drift. `ThoughtButtons` only reports the click.
    //
    // The override exists solely for the gap between a successful mark and the list re-fetch that
    // follows, so the badge lands on the card the author actually pressed rather than a round trip
    // later. It is written from the server's answer and cleared in ONE direction when a fresh DTO
    // arrives - never a two-way sync between the two, which does not converge (rule 31).
    // BOARD placement. Read-only and server-resolved: there is no override and no optimistic path
    // because there is no client-side write that could change it. The placement was frozen when the
    // justification was published, and a member changing their vote afterwards must NOT move this badge
    // — that is the property `thoughts.board_slot_key` exists to hold.
    //
    // Composed here rather than in the JSX so the item-only degradation is one decision in one place:
    // the item can be deleted out from under a thought (ON DELETE SET NULL) while the placement it
    // argued for survives, and a badge naming just the placement beats a badge that vanishes.
    const boardPlacement = openingArgument.openingArgument as
        | (typeof openingArgument.openingArgument & ThoughtBoardPlacementFields)
        | undefined;
    const boardItemLabel = boardPlacement?.boardItemLabel?.trim() || null;
    const boardSlotLabel = boardPlacement?.boardSlotLabel?.trim() || null;
    const boardPlacementBadge = boardItemLabel && boardSlotLabel
        ? localize(localizer, "discussion.board.thought.placement", boardItemLabel, boardSlotLabel)
        : boardItemLabel
            ? localize(localizer, "discussion.board.thought.on_item", boardItemLabel)
            : boardSlotLabel
                ? localize(localizer, "discussion.board.settled.into", boardSlotLabel)
                : null;
    // PASSAGE anchor (design D2 / A2). Read-only and server-resolved like the board placement above:
    // `anchorState` is computed at read against the post's CURRENT source, so nothing here compares
    // hashes or re-derives it. REMOVED keeps the thought (a thread must never silently disappear) and
    // drops only the jump, because the block it would jump to is gone.
    const passageAnchorBlockId = openingArgument.openingArgument?.anchorBlockId?.trim() || null;
    const passageAnchorState = passageAnchorBlockId
        ? parsePassageAnchorState(openingArgument.openingArgument?.anchorState)
        : null;

    const dtoMindChanged = Boolean(openingArgument.openingArgument?.mindChanged);
    const [mindChangedOverride, setMindChangedOverride] = useState<boolean | null>(null);
    const [mindChangedComposerOpen, setMindChangedComposerOpen] = useState(false);
    const [markingMindChanged, setMarkingMindChanged] = useState(false);
    useEffect(() => {
        setMindChangedOverride(null);
        setMindChangedComposerOpen(false);
    }, [dtoMindChanged, openingArgumentPublicId]);
    const isMindChanged = mindChangedOverride ?? dtoMindChanged;

    // Server-authoritative, and there is deliberately no optimistic write: the mark is not
    // retractable (UX 4.4), so showing a badge that a refusal then removes would be the one wrong
    // answer this feature cannot take back. A 403 or a 400 here is a legitimate outcome rather than a
    // bug to retry around - `canMarkMindChanged` can go stale (the post archived, the thought
    // deleted, the 30-day reciprocity window still open) and the server answers with its own copy.
    const submitMindChanged = async (text: string) => {
        if (markingMindChanged || !openingArgumentPublicId) {
            return;
        }
        setMarkingMindChanged(true);
        try {
            await ClientFactory.markMindChanged(openingArgumentPublicId, {
                sourceJson: JSON.stringify({
                    type: "doc",
                    content: [{ type: "paragraph", content: [{ type: "text", text }] }],
                }),
                // REQUIRED, and its absence made this whole mechanic unusable for six weeks
                // (2026-08-09 -> 2026-09-20: `thought_mind_changed` = 0 rows in prod, all-time).
                // The reflection is published through the ORDINARY thought path, which refuses
                // without an idempotency key - `ThoughtService:1249`,
                // `thought.error.client_publish_id_required` - so every submit was a 400. Minted the
                // same way every other publish call site mints one (`usePollVoteFlow`,
                // `useBoardVoteFlow`, `ThoughtTextEditor`). Minted per SUBMIT, not per render: a
                // retry after a failure is a new publish attempt, and the mark itself is idempotent
                // on `thought_id` server-side regardless.
                clientPublishId: createClientUuid(),
            });
            setMindChangedOverride(true);
            setMindChangedComposerOpen(false);
            toast.success(localize(localizer, "discussion.mind_changed.toast"));
            // The reflection is a real thought, so the thread has to reload to show it in position.
            // Reusing the pin callback would be wrong - this is not an ordering change - so the reply
            // tree is refreshed the same way a new reply refreshes it.
            setReplyTreeReloadToken((token) => token + 1);
        } catch (e) {
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            toast.error(getSafeToastMessageFromError(e, localize(localizer, "discussion.mind_changed.prompt")));
        } finally {
            setMarkingMindChanged(false);
        }
    };

    // Reddit-style collapse: the opening argument + its whole thread fold into a single header-only
    // row. We DON'T unmount ThoughtReplyTree when collapsed — that would throw away its fetched
    // subtree and force a full network refetch (+ flash of empty replies) on every re-expand. Instead
    // the reply tree stays mounted and is hidden via `display:none`, so re-expanding is instant.
    const showCollapsedHeader = repliesCollapsed && replyTreeCount > 0;

    /**
     * `DISCUSSION-75` — the "Read as conversation" entry, built once and rendered in ONE of two
     * placements. Design: the linear-chain doc §12.2.
     *
     * <p>`OPENING_HIGHLIGHTED` has exactly one call site (`DiscussionThoughtsDisplayer`'s focused
     * card), so "this card is highlighted" and "the member followed a thought permalink" are the same
     * proposition — which is why the placement keys off the display mode rather than re-reading the
     * URL. A second answer to "what route is this" is how the two halves drift.
     *
     * <p><b>On the permalink arrival</b> the link moves to the TOP of the conversation area (between
     * the anchored thought and its first reply) and takes the chain view's own navigation vocabulary,
     * and the foot copy is not rendered — one link, one screen. <b>On the ordinary list</b> nothing
     * changes: muted, at the foot, exactly as `DISCUSSION-40` (wave-14 Q34) decided.
     *
     * <p><b>This does not reverse Q34.</b> Q34 muted this link because it and `ThoughtThread`'s
     * primary "Continue this thread" sat ~30 CSS px apart offering more of the same thread, so a
     * reader had to guess which reading mode they were choosing. That collision is a property of the
     * FOOT position and is untouched — the list card still renders the muted foot link beside the
     * primary one. At the top of the anchored card nothing competes, so the reason for the muting is
     * not present.
     *
     * <p>The gate is deliberately unchanged: a door promising a conversation must not open onto one
     * node. That state is an ARRIVAL (a pasted link, a share), rendered by `ThoughtChainDisplayer`'s
     * single-node branch — never a destination we send someone to.
     */
    const isPermalinkArrival = displayMode === "OPENING_HIGHLIGHTED";
    const chainEntryLink = chainHref && replyTreeCount > 0 && !repliesCollapsed ? (
        <a
            href={chainHref}
            className={isPermalinkArrival
                // The vocabulary the chain view already uses for its own "Back to post" link, so the
                // two ends of this navigation read as one feature. `min-h-11` on a coarse pointer is
                // DISCUSSION-66's floor, kept: promoting the weight does not promote the hit box.
                ? "ml-10 mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary no-underline hover:underline touch-manipulation [@media(pointer:coarse)]:min-h-11 sm:ml-12"
                : "ml-10 mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground underline decoration-muted-foreground/40 underline-offset-2 transition-colors hover:text-foreground hover:decoration-current touch-manipulation [@media(pointer:coarse)]:min-h-11 sm:ml-12"}
            data-playwright-id="thought-read-as-conversation"
            data-thought-public-id={openingArgumentPublicId}
            // Written in BOTH placements, never omitted on one side, for the same reason
            // `data-highlighted` below is: an absent attribute cannot be told apart from a card that
            // has not mounted, and the negative half is what makes a spec an invariance rather than a
            // tautology. Assert this, never the class string — a restyle must not fail the spec.
            data-chain-link-placement={isPermalinkArrival ? "top" : "foot"}
        >
            <MessagesSquare className={isPermalinkArrival ? "size-4" : "size-3.5"} aria-hidden="true" />
            {localizer("thread.read_as_conversation")}
        </a>
    ) : null;

    return (
        <div
            ref={rootRef}
            className={rootClassName}
            data-playwright-id="discussion-opening-thought-card"
            data-opening-thought-public-id={openingArgumentPublicId ?? ""}
            data-opening-argument-username={openingArgument.openingArgument?.username ?? ""}
            data-opening-thought-pinned={openingArgument.openingArgument?.pinned ? "true" : undefined}
            // The permalink hooks, the SAME pair `ThoughtDisplayerInConversation` carries, because a
            // `/thought/{id}` permalink to a TOP-LEVEL thought lands here rather than there: the focused
            // card renders through this component in `OPENING_HIGHLIGHTED` mode while the list below
            // filters it out. They were added on 2026-08-09 to the conversation component only, so
            // `thought-permalink-highlight.spec.mjs` — which publishes top-level thoughts — could not
            // find its target at all and went red on its first real run (the 8a274b0da gate). The
            // highlight itself was working the whole time; only the assertable hook was missing, which
            // is precisely the failure mode that spec exists to prevent.
            //
            // Written on EVERY card and in BOTH states, never omitted on the negative side: an absent
            // attribute is indistinguishable from a card that has not mounted, and the negative half is
            // what makes the spec an invariance across siblings rather than a tautology.
            data-thought-public-id={openingArgumentPublicId ?? ""}
            data-highlighted={displayMode === "OPENING_HIGHLIGHTED" ? "true" : "false"}
        >
            {/* Rendered here, OUTSIDE the `relative` wrapper below, for two reasons. First, the
                collapse rail inside it is absolutely positioned at a fixed `top-14` offset tuned to the
                avatar; anything inserted above the avatar inside that wrapper would slide the rail off
                the connector line. Second, one render covers every state this card has — collapsed and
                expanded, and both display modes. `displayMode` only swaps `rootClassName`, so there is
                no separate OPENING_HIGHLIGHTED branch to forget: a badge implemented only in the list
                branch would vanish exactly when a user followed a permalink to the pinned thought,
                which is where the focused card renders and the list filters it out. Do not split it. */}
            {isAccepted || pinnedBadgeLabel || isMindChanged || boardPlacementBadge || passageAnchorState ? (
                /* One row owning the `mb-2`, so the gap below is identical whether this card carries the
                   accepted badge, the pinned badge, or both — a question CAN carry both (Q-D1), and letting
                   either badge own the spacing is the 2026-07-24 group-page regression shape: rhythm
                   carried by an element that can disappear. Per rule 23 both are a bordered pill, never a
                   tinted card: a success wash would collide with the sensitive/spoiler gate treatments
                   that can apply to this same card. */
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    {isAccepted ? (
                        <span data-playwright-id="discussion-thought-accepted-badge">
                            <Badge variant="outline" className="gap-1 text-xs font-medium">
                                <Check className="size-3" aria-hidden="true" />
                                {localize(localizer, "discussion.question.accepted.badge")}
                            </Badge>
                        </span>
                    ) : null}
                    {pinnedBadgeLabel ? (
                        <span data-playwright-id="discussion-thought-pinned-badge">
                            <Badge variant="outline" className="gap-1 text-xs font-medium">
                                <Pin className="size-3" aria-hidden="true" />
                                {pinnedBadgeLabel}
                            </Badge>
                        </span>
                    ) : null}
                    {/* CHANGED MY MIND, the third independent slot badge on this card. Independent
                        `@if` rather than a branch, like its two siblings: a branch would make the
                        badges each other's business and the next one would have to edit all of them.

                        Falls back to [Deleted User] rather than disappearing when the post author's
                        account has been erased, following the pinned badge's precedent - the mark is a
                        record of what happened and it survives the account that made it. */}
                    {isMindChanged ? (
                        <span data-playwright-id="discussion-thought-mind-changed-badge">
                            <Badge variant="outline" className="gap-1 text-xs font-medium">
                                <Sparkles className="size-3" aria-hidden="true" />
                                {localizeWithMemberName(localizer, "discussion.mind_changed.badge",
                                    discussionAuthorUsername || localizer("literal.deleted_user"))}
                            </Badge>
                        </span>
                    ) : null}
                    {/* BOARD placement, the fourth independent slot badge. Independent `@if` like the
                        three above, for the reason stated there.

                        This is what makes a §5.1 justification legible as an argument about a specific
                        placement rather than as a stray comment on a busy post. Both halves come off
                        the DTO already resolved: the placement is the one the writing ARGUED for, not
                        the one its author holds now, so nothing here may recompute it from a live board
                        payload. */}
                    {boardPlacementBadge ? (
                        <span data-playwright-id="discussion-thought-board-placement-badge">
                            <Badge variant="outline" className="gap-1 text-xs font-medium">
                                <LayoutList className="size-3" aria-hidden="true" />
                                {boardPlacementBadge}
                            </Badge>
                        </span>
                    ) : null}
                    {/* PASSAGE anchor, the fifth independent slot badge (design §5: an anchored comment
                        is a thought with an anchor, shown in the same tree). ANCHORED and EDITED_SINCE
                        are a button that jumps to the block through the page's block registry, the
                        edited case carrying its note as the title; REMOVED is a plain badge, since the
                        block it named no longer exists and a jump would land somewhere else.
                        `data-anchor-state` is written in every state so a spec can assert the
                        transition rather than the absence of a chip. */}
                    {passageAnchorState === "REMOVED" ? (
                        <span data-playwright-id="passage-chip" data-anchor-state="REMOVED">
                            <Badge variant="outline" className="gap-1 text-xs font-medium text-muted-foreground">
                                <TextQuote className="size-3" aria-hidden="true" />
                                {localize(localizer, "passage.chip.removed")}
                            </Badge>
                        </span>
                    ) : passageAnchorState ? (
                        <button
                            type="button"
                            data-playwright-id="passage-chip"
                            data-anchor-state={passageAnchorState}
                            data-block-id={passageAnchorBlockId ?? ""}
                            className="rounded-full outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            title={passageAnchorState === "EDITED_SINCE"
                                ? localize(localizer, "passage.chip.edited_since")
                                : undefined}
                            onClick={() => {
                                scrollToPassageBlock(discussionPublicId, passageAnchorBlockId);
                            }}
                        >
                            <Badge variant="outline" className="gap-1 text-xs font-medium hover:bg-muted">
                                <TextQuote className="size-3" aria-hidden="true" />
                                {localize(localizer, "passage.chip")}
                            </Badge>
                        </button>
                    ) : null}
                </div>
            ) : null}
            {/* The author-side "you were read" report. Sits with the badges, above the `relative`
                wrapper, for the same two reasons they do: the collapse rail inside that wrapper is
                positioned against the avatar, and one render here covers both display modes rather than
                leaving the report missing exactly when the author follows a permalink to their own
                thought. Renders itself to null for every viewer who is not the author, so there is no
                condition to keep in sync out here. */}
            <FirstReadAuthorReport
                openingArgument={openingArgument}
                loggedInUsername={loggedInUsername}
                localizer={localizer}
            />
            <div className="relative">
                {/* Collapse rail spanning the opening argument's WHOLE reply tree, so the entire
                    vertical thread line collapses the discussion Reddit-style. It lives on this
                    wrapper (not the content box below) so bottom-0 reaches the bottom of
                    ThoughtReplyTree; only the invisible hit area grows — the trunk-line geometry is
                    unchanged. Hidden while collapsed (the collapsed header owns the expand affordance). */}
                {replyTreeCount > 0 && !repliesCollapsed ? (
                    <button
                        type="button"
                        aria-label={localizer("replies.hide", replyTreeCount)}
                        onClick={() => setRepliesCollapsed(true)}
                        onMouseEnter={() => setOpeningRailHovered(true)}
                        onMouseLeave={() => setOpeningRailHovered(false)}
                        className="group/rail absolute left-3 sm:left-4 top-14 bottom-0 z-10 w-6 cursor-pointer touch-manipulation rounded-full bg-transparent [@media(pointer:coarse)]:min-w-11 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        data-playwright-id="thought-collapse-rail"
                        data-thought-public-id={openingArgumentPublicId ?? ""}
                    >
                        {/* Visible collapse handle pinned on the connector line (left-2 +
                            size-4 centers it at the line's 16px-from-rail offset). */}
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute left-2 top-0 flex size-4 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground transition-colors group-hover/rail:border-foreground/70 group-hover/rail:text-foreground"
                        >
                            <Minus className="size-3" />
                        </span>
                    </button>
                ) : null}

                {showCollapsedHeader ? (
                    /* Collapsed header-only row standing in for the opening argument + its whole thread.
                        The reply tree below stays mounted (hidden), so "Show N replies" re-expands
                        instantly with no refetch. */
                    <ThoughtDisplayerInDiscussion
                        argument={openingArgument.openingArgument!}
                        conversationId={null}
                        discussionSlug={discussionSlug}
                        discussionTitle={discussionTitle}
                        isAuthorTheLoggedInUser={openingArgument.openingArgument!.username === loggedInUsername}
                        localizer={localizer}
                        isSensitive={isSensitive}
                        suppressSpoilerGuard={suppressSpoilerGuards}
                        collapsed={true}
                        collapsedReplyCount={replyTreeCount}
                        onToggleCollapsed={() => setRepliesCollapsed(false)}
                    />
                ) : (
                    <>
                        <div className="relative w-full min-w-0 max-w-full">
                            {/* Connector line tying the opening argument's avatar down into its reply rail; the
                                per-reply elbows are drawn by ThreadChildrenBlock inside ThoughtReplyTree. */}
                            {replyTreeCount > 0 ? (
                                <div
                                    className={`pointer-events-none absolute left-7 bottom-0 top-7 w-px ${threadConnectorLineClass(openingRailHovered)} sm:left-8 sm:top-8`}
                                    aria-hidden="true"
                                    data-playwright-id="thought-connector-spine"
                                    data-thought-public-id={openingArgumentPublicId ?? ""}
                                />
                            ) : null}

                            <ThoughtDisplayerInDiscussion
                                argument={openingArgument.openingArgument!}
                                conversationId={null}
                                discussionSlug={discussionSlug}
                                discussionTitle={discussionTitle}
                                isAuthorTheLoggedInUser={openingArgument.openingArgument!.username === loggedInUsername}
                                localizer={localizer}
                                isSensitive={isSensitive}
                                suppressSpoilerGuard={suppressSpoilerGuards}
                                onDiscussClicked={handleDiscussClicked}
                                onQuoteSelection={openReplyWithQuote}
                                pinnedThoughtPublicId={pinnedThoughtPublicId}
                                onPinChanged={onPinChanged}
                                contentWrapperClassName="ml-10 pr-3 sm:ml-12 sm:pr-4"
                                discussionPublicId={discussionPublicId}
                                isQuestion={isQuestion}
                                accepted={isAccepted}
                                onAcceptedChanged={handleAcceptedChanged}
                                mindChanged={isMindChanged}
                                onMarkMindChangedClicked={() => setMindChangedComposerOpen(true)}
                                discussionAuthorUsername={discussionAuthorUsername}
                            />

                            {/* The composer, directly under the card whose thought it is about. It is
                                rendered by the CARD rather than by the action row because the card
                                owns the flag the write updates, and because the sentence is the
                                feature while the button is only its trigger. */}
                            {mindChangedComposerOpen && !isMindChanged ? (
                                <div className="ml-10 pr-3 sm:ml-12 sm:pr-4">
                                    <MindChangedComposer
                                        localizer={localizer}
                                        submitting={markingMindChanged}
                                        onSubmit={(text) => void submitMindChanged(text)}
                                        onCancel={() => setMindChangedComposerOpen(false)}
                                    />
                                </div>
                            ) : null}

                            {matchedDiscussionGroups.length > 0 ? (
                                <div
                                    className="ml-10 mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground sm:ml-12"
                                    data-playwright-id="discussion-opening-thought-group-provenance"
                                >
                                    <span>{localize(localizer, "discussion.thoughts.filter.groups.provenance_label")}</span>
                                    {matchedDiscussionGroups.slice(0, 3).map((group) => (
                                        <Badge key={group.publicId} variant="outline">
                                            {group.name ?? group.publicId}
                                        </Badge>
                                    ))}
                                    {matchedDiscussionGroups.length > 3 ? (
                                        <Badge variant="outline">+{matchedDiscussionGroups.length - 3}</Badge>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        {displayEditor && !isMobile ? (
                            <div className="ml-10 mt-1 sm:ml-12">
                                <ThoughtTextEditor
                                    ref={inlineEditorRef}
                                    localizer={localizer}
                                    agreementAgreementTitle={agreementAgreementTitle}
                                    disagreementAgreementTitle={disagreementAgreementTitle}
                                    isUserLoggedIn={MetaHandler.isUserLoggedIn()}
                                    loggedInUsername={loggedInUsername}
                                    discussionPublicId={discussionPublicId}
                                    parentArgumentPublicId={openingArgumentPublicId}
                                    autoFocus={true}
                                    onThoughtPublished={handleInlineReplyPublished}
                                    playwrightIdPrefix="discussion-inline-reply-editor"
                                    hideAvatarOnMobile={true}
                                />
                            </div>
                        ) : null}
                    </>
                )}

                {/* DISCUSSION-75 — the promoted placement: at the TOP of the conversation area, above
                    the first reply, on a permalink arrival only. It appears when the reply tree
                    appears (both wait on `replyTreeCount`), immediately above it, so the reflow is
                    bounded and sits BELOW the thought the member came to read. See the construction
                    of `chainEntryLink` above for why this does not reverse Q34. */}
                {isPermalinkArrival ? chainEntryLink : null}

                {/* Reply tree: always mounted so collapse/expand is pure client state (no refetch);
                    hidden via display:none while collapsed. Kept inside this `relative` wrapper so the
                    collapse rail's bottom-0 still spans it when expanded. */}
                {openingArgumentPublicId ? (
                    <div className={repliesCollapsed ? "hidden" : undefined}>
                        <ThoughtReplyTree
                            key={replyTreeReloadToken}
                            common={replyTreeCommon}
                            rootArgumentPublicId={openingArgumentPublicId}
                            maxDepth={replyTreeMaxDepth}
                            onCountChange={setReplyTreeCount}
                            railHovered={openingRailHovered}
                        />
                    </div>
                ) : null}

                {/* Read the strongest single lineage under this thought as a continuous conversation. */}
                {/* The builder returns null when either segment is unusable, so an unbuildable link is
                    not rendered at all rather than rendered pointing at `/thought/null`. */}
                {/* `DISCUSSION-40` (wave-14 Q34) — the SECONDARY of two continuation links, and it is
                    styled as one. This link and `ThoughtThread`'s "Continue this thread" sit about
                    30 CSS px apart and both offer more of the same thread; before this they were both
                    primary-coloured and differed only in `text-sm` vs `text-xs`, so a reader had to
                    guess which reading mode they were choosing and a wrong guess cost a navigation
                    plus a Back. Q34 chose weight over words deliberately, because a clarifying label
                    would cost 25 catalogs and the weight is free. So: muted at rest, foreground on
                    hover, no `font-medium`. The primary link is untouched — it stays the obvious
                    continuation, which is the half that makes the pair legible. Flat-X: one hover
                    transition, semantic tokens, no shadow, no border-colour flip. */}
                {/* DISCUSSION-66, two changes, and NEITHER reverses Q34 above.
                    (1) The coarse-pointer height floor: this was a bare inline anchor at `text-xs`, so
                        its hit box was a ~16 CSS px line box stacked ~30 CSS px from the primary link.
                        `min-h-11` rather than `touch-hit-44` because an overlay slug would collide with
                        that sibling (03-tap-targets.css:70); growing the layout box instead also pushes
                        the pair apart. Mouse density is untouched.
                    (2) A REST-STATE link signal. Q34 bought this link's rank with `hover:underline`,
                        and hover is the one interaction the priority viewport cannot perform — so on a
                        phone it was 12 px muted text beside a 14 px icon with nothing saying it was a
                        link at all. The underline now exists at rest, and the RANK is still carried
                        entirely by what Q34 chose: muted colour, `text-xs`, no `font-medium`. The
                        decoration is drawn at 40% so it reads as a quiet link rather than a second
                        primary, and hover still lifts both the text and the line to `foreground`. */}
                {/* DISCUSSION-75 — the foot placement is now the LIST card's placement only. On a
                    permalink arrival the same link is rendered above the reply tree instead, so it
                    never appears twice on one screen. */}
                {isPermalinkArrival ? null : chainEntryLink}
            </div>
        </div>
    );
}
